import { z } from "zod";
import { ArticleInquiry } from "./articleInquiry";
import { Attachment } from "./attachment.firestore";
import { CatalogItemPreview } from "./catalogItem";

export type { Attachment } from "./attachment.firestore";

import { BookingInMessage, BookingToConfirmInMemberMessage } from "./booking";
import { Mention } from "./mention";
import { NotificationAttempts } from "./notification";
import { AgentId, AlbertineServiceAgentId, FirebaseId, MemberId } from "./primitives";
import { QuickRequest } from "./quickRequest";
import { FirebaseTimestamp } from "./record.firestore";
import { Nomination } from "./referrals";
import { CatalogArticleItem } from "./storyblok";

// TODO: Copied (partially)from firebase/lmt/src/common/types. Replace the file there with imports from this file and move tests if any.

export const EmojiReaction = z.enum(["👍", "👎", "🙏", "❤️", "😊", "😞", "✅"]);
export type EmojiReaction = z.infer<typeof EmojiReaction>;

export const MemberReaction = z.object({
    memberReaction: EmojiReaction,
    memberReactionTimestamp: FirebaseTimestamp,
});
export type MemberReaction = z.infer<typeof MemberReaction>;
const MemberReactionUndefined = z.object({
    memberReaction: z.undefined(),
    memberReactionTimestamp: z.undefined(),
});

export const MessageMeta = z.object({
    isInternalNote: z.literal(true).optional(),
    additionalNotificationAttempts: NotificationAttempts.optional(),
});

export const MessageRequest = z.object({
    request: FirebaseId.nullable().optional(),
    requestTitle: z.string().nullable().optional(),
});

export type MessageRequest = z.infer<typeof MessageRequest>;

export const MessageLink = z.object({
    url: z.url(),
});

export type MessageLink = z.infer<typeof MessageLink>;

export const MessageBody = z
    .object({
        message: z.string(),
        senderName: z.string(),
        senderFirstName: z.string().nullable().optional(),
        sentBy: AgentId.or(MemberId).or(AlbertineServiceAgentId),
        sendingId: z.string().nullable().optional(),

        member: MemberId.nullable().optional(),
        memberName: z.string().nullable().optional(),

        editedAt: FirebaseTimestamp.nullable().optional(),
        editedBy: z.string().nullable().optional(),
        editorName: z.string().nullable().optional(),
        messageHtml: z.string().optional().nullable(),
        sentByAgent: z.boolean().optional(),
        updatedAt: FirebaseTimestamp.nullable().optional(),

        link: MessageLink.optional(),

        attachment: Attachment.optional(), // deprecated
        attachments: z.array(Attachment).optional(),

        confirmedBooking: BookingToConfirmInMemberMessage.optional().nullable(),
        bookingSuggestions: z.array(BookingInMessage).optional().nullable(),

        articles: z.array(CatalogArticleItem).optional(), // deprecated but still in use for education and activation messages
        articlesV2: z.array(CatalogItemPreview).optional(),

        nomination: Nomination.optional().nullable(),
    })
    .extend(MessageRequest.shape);

export type MessageBody = z.infer<typeof MessageBody>;

export const MessageBodyWithSentAt = z
    .object({
        sentAt: FirebaseTimestamp,
    })
    .extend(MessageBody.shape);

export type MessageBodyWithSentAt = z.infer<typeof MessageBodyWithSentAt>;

export const MessageWithArticleInquiry = z.object({
    articleInquiry: ArticleInquiry.optional().nullable(), // deprecated from app version onwards 1.4.18
});
export type MessageWithArticleInquiry = z.infer<typeof MessageWithArticleInquiry>;

export const MessageWithQuickRequest = z.object({
    quickRequest: QuickRequest.optional().nullable(),
});
export type MessageWithQuickRequest = z.infer<typeof MessageWithQuickRequest>;

export const ReplyToMessageOptional = z.object({
    replyToMessageId: FirebaseId.optional(),
    replyToMessage: MessageBody.omit({ senderName: true, sentBy: true }).optional(),
});

export const MessageDoc = MessageMeta.extend(MessageBodyWithSentAt.shape)
    .extend(MessageWithArticleInquiry.shape)
    .extend(MessageWithQuickRequest.shape)
    .and(MemberReaction.or(MemberReactionUndefined))
    .and(ReplyToMessageOptional);
export type MessageDoc = z.infer<typeof MessageDoc>;

export const Message = z.object({ id: FirebaseId }).and(MessageDoc);
export type Message = z.infer<typeof Message>;

export const InternalNote = MessageMeta.extend(MessageBodyWithSentAt.shape).extend(
    z.object({
        id: FirebaseId,
        isInternalNote: z.literal(true),
    }).shape,
);
export type InternalNote = z.infer<typeof InternalNote>;

export const InternalNoteWithMention = InternalNote.and(Mention);
export type InternalNoteWithMention = z.infer<typeof InternalNoteWithMention>;

// Prefer WithMention first: InternalNote.or(WithMention) would match InternalNote
// and strip mention fields under Zod's default unknown-key stripping.
export const InternalNoteOrMention = InternalNoteWithMention.or(InternalNote);

export type InternalNoteOrMention = z.infer<typeof InternalNoteOrMention>;

export type ParseInternalNoteOrMentionResult =
    | { success: true; data: InternalNoteOrMention }
    | {
          success: false;
          withMentionError: z.ZodError;
          withoutMentionError: z.ZodError;
      };

/**
 * Prefer WithMention first so mention fields are not stripped by InternalNote.
 * Use this instead of InternalNoteOrMention.safeParse when logging both failures.
 */
export function parseInternalNoteOrMention(
    data: unknown,
): ParseInternalNoteOrMentionResult {
    const withMention = InternalNoteWithMention.safeParse(data);
    if (withMention.success) {
        return { success: true, data: withMention.data };
    }

    const withoutMention = InternalNote.safeParse(data);
    if (withoutMention.success) {
        return { success: true, data: withoutMention.data };
    }

    return {
        success: false,
        withMentionError: withMention.error,
        withoutMentionError: withoutMention.error,
    };
}

export const LatestMessageMeta = z.object({
    id: FirebaseId,
    sentAt: FirebaseTimestamp,
    sentBy: AgentId.or(MemberId).or(AlbertineServiceAgentId),
    sentByAgent: z.boolean().optional(),
});

export type LatestMessageMeta = z.infer<typeof LatestMessageMeta>;
